<template>
  <div
    class="h-screen bg-white border-r border-gray-200 transition-all duration-300 ease-in-out relative flex flex-col shadow-sm"
    :class="isExpanded ? 'w-64' : 'w-20'"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Logo Section -->
    <div class="flex items-center justify-center h-16 px-4 border-b border-gray-200 bg-gradient-to-r from-[#B70000] to-[#950000]">
      <div class="w-9 h-9 bg-white rounded-lg flex items-center justify-center shrink-0 shadow-md">
        <img src="/pitcar-modified.png" alt="PITCAR" class="w-6 h-6" />
      </div>
      <div 
        class="ml-3 transition-all duration-300 overflow-hidden whitespace-nowrap"
        :class="isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
      >
        <img src="/pitcar-logo.png" alt="PITCAR" class="w-32 h-8 brightness-0 invert" />
      </div>
    </div>

    <!-- Scrollable Menu Container -->
    <nav class="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-4 px-3">
      <!-- Overview -->
      <div class="mb-6">
        <router-link
          :to="'/dashboard/overview'"
          custom
          v-slot="{ isActive, href, navigate }"
        >
          <a
            :href="href"
            @click="navigate"
            class="group relative flex items-center cursor-pointer transition-all duration-200 rounded-lg"
            :class="[
              isActive 
                ? 'text-white bg-gradient-to-r from-[#B70000] to-[#950000] shadow-md' 
                : 'text-gray-700 hover:text-[#B70000] hover:bg-red-50'
            ]"
          >
            <div class="h-11 w-full flex items-center px-3">
              <div class="relative">
                <HomeIcon 
                  class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110"
                  :class="isActive ? 'text-white' : 'text-gray-600'"
                />
                <!-- Active indicator dot -->
                <div 
                  v-if="isActive"
                  class="absolute -right-1 -top-1 w-2 h-2 bg-white rounded-full animate-pulse"
                ></div>
              </div>
              <span
                class="ml-3 whitespace-nowrap transition-all duration-300 font-medium overflow-hidden"
                :class="[
                  isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0',
                  isActive ? 'text-white' : 'text-gray-700'
                ]"
              >
                Overview
              </span>
            </div>
          </a>
        </router-link>
      </div>

      <!-- Statistics Menu Group -->
      <div class="mb-6">
        <div 
          class="flex items-center px-3 h-10 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 cursor-pointer rounded-lg transition-colors hover:text-[#B70000]"
          :class="isExpanded ? 'justify-between' : 'justify-center'"
          @click="toggleStats"
        >
          <div class="flex items-center">
            <ChartPieIcon class="w-5 h-5 min-w-[20px]" />
            <span 
              class="transition-all duration-300 overflow-hidden ml-3"
              :class="isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
            >
              Statistics
            </span>
          </div>
          <ChevronDownIcon 
            v-if="isExpanded"
            class="w-4 h-4 transition-transform duration-200"
            :class="showStats ? 'rotate-180' : ''"
          />
        </div>

        <!-- Statistics Submenu -->
        <div 
          v-show="showStats && isExpanded" 
          class="space-y-1 pl-2"
        >
          <router-link
            v-for="item in statsItems"
            :key="item.path"
            :to="item.path"
            custom
            v-slot="{ isActive, href, navigate }"
          >
            <a
              :href="href"
              @click="navigate"
              class="group relative flex items-center cursor-pointer transition-all duration-200 rounded-lg"
              :class="[
                isActive 
                  ? 'text-white bg-gradient-to-r from-[#B70000] to-[#950000] shadow-sm' 
                  : 'text-gray-600 hover:text-[#B70000] hover:bg-red-50'
              ]"
            >
              <div class="h-10 w-full flex items-center px-3">
                <component 
                  :is="item.icon" 
                  class="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:scale-110"
                  :class="isActive ? 'text-white' : 'text-gray-500'"
                />
                <span
                  class="ml-3 whitespace-nowrap transition-all duration-300 text-sm font-medium overflow-hidden"
                  :class="[
                    isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0',
                    isActive ? 'text-white' : 'text-gray-700'
                  ]"
                >
                  {{ item.name }}
                </span>
              </div>
            </a>
          </router-link>
        </div>
      </div>

      <!-- KPI Menu Group -->
      <div class="mb-6">
        <div 
          class="flex items-center px-3 h-10 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 cursor-pointer rounded-lg transition-colors hover:text-[#B70000]"
          :class="isExpanded ? 'justify-between' : 'justify-center'"
          @click="toggleKPI"
        >
          <div class="flex items-center">
            <ChartBarIcon class="w-5 h-5 min-w-[20px]" />
            <span 
              class="transition-all duration-300 overflow-hidden ml-3"
              :class="isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
            >
              KPI Dashboard
            </span>
          </div>
          <ChevronDownIcon 
            v-if="isExpanded"
            class="w-4 h-4 transition-transform duration-200"
            :class="showKPI ? 'rotate-180' : ''"
          />
        </div>

        <!-- KPI Submenu -->
        <div 
          v-show="showKPI && isExpanded" 
          class="space-y-1 pl-2"
        >
          <router-link
            v-for="item in kpiItems"
            :key="item.path"
            :to="item.path"
            custom
            v-slot="{ isActive, href, navigate }"
          >
            <a
              :href="href"
              @click="navigate"
              class="group relative flex items-center cursor-pointer transition-all duration-200 rounded-lg"
              :class="[
                isActive 
                  ? 'text-white bg-gradient-to-r from-[#B70000] to-[#950000] shadow-sm' 
                  : 'text-gray-600 hover:text-[#B70000] hover:bg-red-50'
              ]"
            >
              <div class="h-10 w-full flex items-center px-3">
                <component 
                  :is="item.icon" 
                  class="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:scale-110"
                  :class="isActive ? 'text-white' : 'text-gray-500'"
                />
                <span
                  class="ml-3 whitespace-nowrap transition-all duration-300 text-sm font-medium overflow-hidden"
                  :class="[
                    isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0',
                    isActive ? 'text-white' : 'text-gray-700'
                  ]"
                >
                  {{ item.name }}
                </span>
                <!-- Optional navigation badge -->
                <span 
                  v-if="item.badge || item.isNew"
                  class="ml-auto px-2 py-0.5 text-[10px] font-bold bg-green-500 text-white rounded-full transition-all duration-300 overflow-hidden whitespace-nowrap"
                  :class="isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
                >
                  {{ item.badge || 'NEW' }}
                </span>
              </div>
            </a>
          </router-link>
        </div>
      </div>

      <!-- AI Assistant Menu -->
      <div class="mb-6">
        <router-link
          :to="'/dashboard/ai-assistant/'"
          custom
          v-slot="{ isActive, href, navigate }"
        >
          <a
            :href="href"
            @click="navigate"
            class="group relative flex items-center cursor-pointer transition-all duration-200 rounded-lg"
            :class="[
              isActive 
                ? 'text-white bg-gradient-to-r from-[#B70000] to-[#950000] shadow-md' 
                : 'text-gray-700 hover:text-[#B70000] hover:bg-red-50'
            ]"
          >
            <div class="h-11 w-full flex items-center px-3">
              <div class="relative">
                <SparklesIcon 
                  class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110"
                  :class="isActive ? 'text-white animate-pulse' : 'text-gray-600'"
                />
              </div>
              <span
                class="ml-3 whitespace-nowrap transition-all duration-300 font-medium overflow-hidden"
                :class="[
                  isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0',
                  isActive ? 'text-white' : 'text-gray-700'
                ]"
              >
                AI Assistant
              </span>
            </div>
          </a>
        </router-link>
      </div>

    </nav>

    <!-- Bottom Section -->
    <div class="border-t border-gray-200 bg-gray-50">
      <!-- Version info -->
      <div 
        class="px-4 py-2 text-xs text-gray-400 text-center transition-all duration-300"
        :class="isExpanded ? 'opacity-100' : 'opacity-0'"
      >
        <span class="font-mono">v{{ version }}</span>
      </div>

      <!-- User Profile Button -->
      <div class="p-3">
        <div class="relative">
          <button 
            class="w-full flex items-center p-2 rounded-lg transition-all duration-200 hover:bg-white hover:shadow-sm"
            :class="showDropdown ? 'bg-white shadow-sm' : ''"
            @click="showDropdown = !showDropdown"
          >
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-[#B70000] to-[#950000] overflow-hidden shrink-0 flex items-center justify-center shadow-md">
              <UserIcon class="w-5 h-5 text-white" />
            </div>
            <div 
              class="ml-3 transition-all duration-300 overflow-hidden whitespace-nowrap text-left flex-1"
              :class="isExpanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'"
            >
              <div class="font-semibold text-gray-900 text-sm truncate">
                {{ authStore.user?.name || 'User' }}
              </div>
              <div class="text-xs text-gray-500">
                {{ authStore.user?.is_admin ? 'Administrator' : 'User' }}
              </div>
            </div>
            <ChevronDownIcon 
              v-if="isExpanded"
              class="w-4 h-4 text-gray-400 transition-transform duration-200 ml-2"
              :class="showDropdown ? 'rotate-180' : ''"
            />
          </button>

          <!-- Dropdown Menu -->
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div 
              v-if="showDropdown && isExpanded"
              class="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            >
              <button 
                class="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                @click="handleProfile"
              >
                <UserCircleIcon class="w-4 h-4 mr-3 text-gray-400" />
                <span class="font-medium">Profile</span>
              </button>
              <div class="border-t border-gray-100 my-1"></div>
              <button 
                class="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                @click="handleLogout"
              >
                <ArrowRightOnRectangleIcon class="w-4 h-4 mr-3" />
                <span class="font-medium">Logout</span>
              </button>
            </div>
          </transition>
        </div>
      </div>

      <button
        type="button"
        class="mx-auto mt-3 flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold text-[#b70000] hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b70000]/30"
        :title="`Catatan perubahan v${version}`"
        @click="showChangelog = true"
      >
        <SparklesIcon class="h-3.5 w-3.5 shrink-0" />
        <span v-if="isExpanded">Apa yang baru?</span>
      </button>
      <p class="mt-0.5 text-center text-[11px] font-medium text-gray-400">
        Jarvis v{{ appVersion }}
      </p>
      <ChangelogModal :open="showChangelog" :current-version="version" @close="showChangelog = false" />
    </div>
  </div>
</template>

<script setup>
const appVersion = __APP_VERSION__
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import packageJson from '../../package.json'
import ChangelogModal from './ChangelogModal.vue'

import {
  HomeIcon,
  ChartPieIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  UserIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ComputerDesktopIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
  HandRaisedIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const authStore = useAuthStore()
const isExpanded = ref(false)
const showChangelog = ref(false)
const showDropdown = ref(false)
const showStats = ref(true)
const showKPI = ref(true)

// Statistics items
const statsItems = [
  { 
    name: 'Service Advisor', 
    icon: UserIcon, 
    path: '/dashboard/service-advisor' 
  },
  { 
    name: 'Mekanik', 
    icon: Cog6ToothIcon,
    path: '/dashboard/mechanics' 
  },
  {
    name: 'Rekomendasi Ditolak',
    icon: HandRaisedIcon,
    path: '/dashboard/rekomendasi-ditolak'
  },
]

// KPI items - WITH NEW KPI OUTLET!
const kpiItems = [
  { 
    name: 'KPI Outlet', 
    icon: BuildingStorefrontIcon, 
    path: '/dashboard/kpi/outlet',
    isNew: true // NEW badge
  },
  {
    name: 'KPI Outlet V4',
    icon: BuildingStorefrontIcon,
    path: '/dashboard/kpi/outlet-v4',
    badge: 'V4'
  },
  // Bayangan: hitungan baru (Journey V4) untuk dibandingkan; menu di atas tetap resmi.
  {
    name: 'KPI Outlet Baru',
    icon: BeakerIcon,
    path: '/dashboard/kpi/outlet-baru',
    badge: 'BETA'
  },
  {
    name: 'SOP Sampling Mockup',
    icon: ClipboardDocumentCheckIcon,
    path: '/mockup/sop-sampling',
    badge: 'NEW'
  },
  { 
    name: 'Customer Support', 
    icon: UserGroupIcon, 
    path: '/dashboard/kpi/customer-support' 
  },
  { 
    name: 'Mekanik', 
    icon: WrenchScrewdriverIcon,
    path: '/dashboard/kpi/mechanic' 
  },
  { 
    name: 'Marketing', 
    icon: PresentationChartLineIcon,
    path: '/dashboard/kpi/marketing' 
  },
  { 
    name: 'Kaizen', 
    icon: ArrowTrendingUpIcon,
    path: '/dashboard/kpi/kaizen' 
  },
  { 
    name: 'IT', 
    icon: ComputerDesktopIcon,
    path: '/dashboard/kpi/it' 
  }
]

const version = packageJson.version

const handleMouseEnter = () => {
  isExpanded.value = true
}

const handleMouseLeave = () => {
  isExpanded.value = false
  showDropdown.value = false
}

const toggleStats = () => {
  if (isExpanded.value) {
    showStats.value = !showStats.value
  }
}

const toggleKPI = () => {
  if (isExpanded.value) {
    showKPI.value = !showKPI.value
  }
}

const handleProfile = () => {
  showDropdown.value = false
  router.push('/profile')
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// Close dropdown when clicking outside
const closeDropdown = (e) => {
  if (showDropdown.value && !e.target.closest('.relative')) {
    showDropdown.value = false
  }
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', closeDropdown)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
})
</script>

<style scoped>
/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #D1D5DB;
}

/* Smooth transitions */
* {
  transition-property: color, background-color, border-color, transform, opacity;
  transition-duration: 200ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active link styling override */
.router-link-active {
  background-color: inherit;
}

/* Dropdown animations */
.absolute {
  transition: all 0.2s ease-in-out;
}

/* Hover effects */
.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

/* Pulse animation for active indicator */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
